/**
 * TypeScript types for Recruit CRM feature
 */

/**
 * Recruitment record representing a creator recruited to the CRM
 */
export interface Recruitment {
  id: string;
  userId: string | null; // null for external creators (email only)
  profileId: string | null; // null when userId is null or profile not yet created
  email: string | null; // present for external creators
  firstName: string | null;
  lastName: string | null;
  agencyId: string;
  status: RecruitmentStatus;
  removed: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Valid recruitment statuses (state machine)
 * Flow: invited → pending_social_linking → pending_vetting → vetting_in_progress → active
 *                                                                ↕
 *                                                          vetting_failed
 *
 * - invited: External creator invited by email (triggers invitation email)
 * - pending_social_linking: Creator needs to connect social media accounts
 * - pending_vetting: Creator has social accounts, awaiting brand safety report
 * - vetting_in_progress: Brand safety report is being processed
 * - vetting_failed: Brand safety report failed
 * - active: Creator completed vetting with ready brand safety report
 */
export const RECRUITMENT_STATUSES = [
  'invited',
  'pending_social_linking',
  'pending_vetting',
  'vetting_in_progress',
  'vetting_failed',
  'active',
] as const;
export type RecruitmentStatus = (typeof RECRUITMENT_STATUSES)[number];

/**
 * Extended status options for the filter dropdown (includes 'removed' pseudo-status)
 */
export const RECRUITMENT_FILTER_STATUSES = [
  ...RECRUITMENT_STATUSES,
  'removed',
] as const;
export type RecruitmentFilterStatus =
  (typeof RECRUITMENT_FILTER_STATUSES)[number];

export const RECRUITMENT_STATUS_PENDING_VETTING: RecruitmentStatus =
  'pending_vetting';
export const RECRUITMENT_STATUS_VETTING_FAILED: RecruitmentStatus =
  'vetting_failed';

/**
 * Sort order options
 */
export const SORT_ORDERS = ['asc', 'desc'] as const;
export type SortOrder = (typeof SORT_ORDERS)[number];

/**
 * Valid sort fields for recruitments
 */
export const RECRUITMENT_SORT_BY_OPTIONS = [
  'created_at',
  'status',
  'name',
  'email',
] as const;
export type RecruitmentSortBy = (typeof RECRUITMENT_SORT_BY_OPTIONS)[number];

/**
 * Filter state for Recruit CRM table controls
 */
export interface RecruitmentFiltersState {
  status?: RecruitmentFilterStatus[];
}

/**
 * Sorting state for Recruit CRM table controls
 */
export interface RecruitmentSortingState {
  sortBy: RecruitmentSortBy;
  sortOrder: SortOrder;
}

/**
 * API response for list recruitments endpoint
 */
export interface RecruitmentsResponse {
  recruitments: Recruitment[];
  meta: {
    currentPage: number;
    perPage: number;
    totalCount: number;
    totalPages: number;
  };
}

/**
 * Raw API recruitment (nested user) – list endpoint response item
 */
export interface ApiRecruitment {
  id: string;
  userId: string | null;
  email: string | null;
  agencyId: string;
  status: RecruitmentStatus;
  removed: boolean;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: number;
    firstName: string;
    lastName: string;
    profile?: {id: string} | null;
  } | null;
}

/**
 * Raw API response for list recruitments endpoint (before mapping to Recruitment[])
 */
export interface ApiRecruitmentsResponse {
  recruitments: ApiRecruitment[];
  meta: {
    currentPage: number;
    perPage: number;
    totalCount: number;
    totalPages: number;
  };
}

/**
 * Filters for recruitment query
 */
export interface RecruitmentFilters {
  status?: RecruitmentFilterStatus[];
  sortBy?: RecruitmentSortBy;
  sortOrder?: SortOrder;
}

/**
 * Pagination params
 */
export interface RecruitmentPagination {
  page: number;
  perPage: number;
}

/**
 * Query parameters for list recruitments
 */
export interface ListRecruitmentsParams
  extends RecruitmentFilters,
    RecruitmentPagination {
  agencyId: string;
}

/**
 * Request params for bulk email
 */
export interface BulkEmailParams {
  agencyId: string;
  crmRecruitmentIds: string[];
  subject: string;
  body: string;
}

/**
 * Response for bulk email
 */
export interface BulkEmailResponse {
  message: string;
  recipientCount: number;
}

/**
 * Request params for bulk threads
 */
export interface BulkThreadsParams {
  agencyId: string;
  crmRecruitmentIds: string[];
  brandId: string;
  initialMessage: string;
}

/**
 * Response for bulk threads
 */
export interface BulkThreadsResponse {
  jobId: string;
  total: number;
  skippedExternal: number;
  skippedExternalIds: string[];
}

/**
 * Request params for remove recruitment
 */
export interface RemoveRecruitmentParams {
  recruitmentId: string;
  agencyId: string;
}

/**
 * Response for remove recruitment
 */
export interface RemoveRecruitmentResponse {
  recruitment: {
    id: string;
    status: string;
    removed: boolean;
  };
}

export type {
  BulkRemoveRecruitmentsParams,
  BulkRemoveRecruitmentsResponse,
} from '@/types/recruitment';

/**
 * Request params for bulk request vetting
 */
export interface BulkRequestVettingParams {
  agencyId: string;
  crmRecruitmentIds: string[];
  brandId: string;
}

/**
 * Response for bulk request vetting
 */
export interface BulkRequestVettingResponse {
  success: string[];
  failures: {
    notFound?: string[];
    notEligible?: string[];
    error?: string[];
  };
}
